export interface RmaPart {
  id: string;
  name: string;
  cost: number;
  qty: number;
}

export interface JobNote {
  id: string;
  author: string;
  text: string;
  timestamp: string;
  type: 'tech' | 'client' | 'system';
}

export type RmaStatus = 'Intake' | 'Diagnostics' | 'Awaiting Parts' | 'Repairing' | 'Testing' | 'Ready' | 'Delivered';
export type RmaPriority = 'Low' | 'Medium' | 'High' | 'Urgent';
export type DeviceCategory = 'Phone' | 'Laptop' | 'Tablet' | 'Console' | 'Other';

export interface RmaJob {
  id: string; // e.g. RMA-2026-0001
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  deviceCategory: DeviceCategory;
  deviceBrand: string;
  deviceModel: string;
  serialNumber: string;
  reportedProblem: string;
  diagnostics: string;
  priority: RmaPriority;
  status: RmaStatus;
  assignedTo: string;
  estimatedCost: number;
  advancePayment: number;
  actualCost: number;
  createdAt: string;
  updatedAt: string;
  notes: JobNote[];
  parts: RmaPart[];
  images?: string[];
}

const STORAGE_KEY = 'openrma_jobs';

// Generate a random ID
const generateId = () => Math.random().toString(36).substr(2, 9);

// Default seed data
const SEED_JOBS: RmaJob[] = [
  {
    id: 'RMA-2026-0001',
    customerName: 'Alice Cooper',
    customerPhone: '+1 (555) 019-2834',
    customerEmail: 'alice.c@example.com',
    deviceCategory: 'Phone',
    deviceBrand: 'Apple',
    deviceModel: 'iPhone 14 Pro',
    serialNumber: 'G06X29A2M09P',
    reportedProblem: 'Cracked screen and back glass after drop. Touch input unresponsive on bottom third of screen.',
    diagnostics: 'Digitizer display is fractured. Touch controller IC appears functional but glass damage severed flex cable routes on lower side. Back panel glass completely shattered. NFC coil operates normally.',
    priority: 'High',
    status: 'Awaiting Parts',
    assignedTo: 'Marcus Vance (Senior Tech)',
    estimatedCost: 280,
    advancePayment: 50,
    actualCost: 280,
    createdAt: '2026-08-10T09:30:00Z',
    updatedAt: '2026-08-11T14:45:00Z',
    parts: [
      { id: 'p1', name: 'iPhone 14 Pro OEM OLED Screen Assembly', cost: 180, qty: 1 },
      { id: 'p2', name: 'iPhone 14 Pro Back Glass Housing with Adhesive', cost: 45, qty: 1 }
    ],
    notes: [
      {
        id: 'n1',
        author: 'System',
        text: 'RMA Ticket created and device received in Intake status.',
        timestamp: '2026-08-10T09:30:00Z',
        type: 'system'
      },
      {
        id: 'n2',
        author: 'Marcus Vance',
        text: 'Diagnostic check complete: Display touch is completely broken due to digitizer fracture. Frame has minor denting near power button but structural integrity is OK. Parts ordered from main supplier.',
        timestamp: '2026-08-10T11:20:00Z',
        type: 'tech'
      },
      {
        id: 'n3',
        author: 'System',
        text: 'Status updated from Diagnostics to Awaiting Parts.',
        timestamp: '2026-08-11T14:45:00Z',
        type: 'system'
      }
    ]
  },
  {
    id: 'RMA-2026-0002',
    customerName: 'David Miller',
    customerPhone: '+1 (555) 021-9988',
    customerEmail: 'david.m@example.com',
    deviceCategory: 'Laptop',
    deviceBrand: 'Apple',
    deviceModel: 'MacBook Pro 16" (M1 Pro, 2021)',
    serialNumber: 'C02FT59QMD6T',
    reportedProblem: 'Spilled coffee over keyboard. Unit shut down immediately and now refuses to turn on or accept charge.',
    diagnostics: 'Heavy corrosion visible around the PMIC (Power Management Integrated Circuit) and charging rails (PP3V3_G3H). Liquid indicators tripped. Ultrasonic board cleaning required.',
    priority: 'Urgent',
    status: 'Repairing',
    assignedTo: 'Sarah Connor (Board Repair specialist)',
    estimatedCost: 650,
    advancePayment: 150,
    actualCost: 650,
    createdAt: '2026-08-11T11:15:00Z',
    updatedAt: '2026-08-12T16:00:00Z',
    parts: [
      { id: 'p3', name: 'ISL9240 Charging IC Chip', cost: 15, qty: 1 },
      { id: 'p4', name: 'Solder Paste & Ultrasonic Solvent Clean Fee', cost: 35, qty: 1 }
    ],
    notes: [
      {
        id: 'n4',
        author: 'System',
        text: 'RMA Ticket created and device received in Intake status.',
        timestamp: '2026-08-11T11:15:00Z',
        type: 'system'
      },
      {
        id: 'n5',
        author: 'Sarah Connor',
        text: 'Main logic board extracted. Liquid residues found around USB-C power input controllers. Board placed in ultrasonic cleaner bath.',
        timestamp: '2026-08-11T15:30:00Z',
        type: 'tech'
      },
      {
        id: 'n6',
        author: 'System',
        text: 'Status updated from Diagnostics to Repairing.',
        timestamp: '2026-08-12T10:00:00Z',
        type: 'system'
      },
      {
        id: 'n7',
        author: 'Sarah Connor',
        text: 'Finished board wash. Re-soldered corroded resistors on the charging circuit. Now checking PPBUS_AON rails. Still shorting, probing nearby caps.',
        timestamp: '2026-08-12T16:00:00Z',
        type: 'tech'
      }
    ]
  },
  {
    id: 'RMA-2026-0003',
    customerName: 'Elena Rostova',
    customerPhone: '+1 (555) 077-4433',
    customerEmail: 'elena.r@example.com',
    deviceCategory: 'Console',
    deviceBrand: 'Sony',
    deviceModel: 'PlayStation 5',
    serialNumber: 'CFI-1215A-0922',
    reportedProblem: 'HDMI port is damaged. The pins inside the port are bent and pushed back. No signal on TV.',
    diagnostics: 'Standard HDMI port wear/damage. Pins 3, 4, 7 completely detached from pad. Pad damage is minor. HDMI encoder IC checks out fine under diode testing.',
    priority: 'Medium',
    status: 'Ready',
    assignedTo: 'Ken Toyoda',
    estimatedCost: 120,
    advancePayment: 0,
    actualCost: 120,
    createdAt: '2026-08-12T08:00:00Z',
    updatedAt: '2026-08-13T10:00:00Z',
    parts: [
      { id: 'p5', name: 'Premium PS5 HDMI Port (OEM Spec)', cost: 12, qty: 1 }
    ],
    notes: [
      {
        id: 'n8',
        author: 'System',
        text: 'RMA Ticket created and device received in Intake status.',
        timestamp: '2026-08-12T08:00:00Z',
        type: 'system'
      },
      {
        id: 'n9',
        author: 'Ken Toyoda',
        text: 'Removed the damaged HDMI port using hot air station (420C). Cleaned the pads and applied leaded solder. Solder joint looks robust.',
        timestamp: '2026-08-12T14:00:00Z',
        type: 'tech'
      },
      {
        id: 'n10',
        author: 'System',
        text: 'Status updated from Repairing to Testing.',
        timestamp: '2026-08-13T08:00:00Z',
        type: 'system'
      },
      {
        id: 'n11',
        author: 'Ken Toyoda',
        text: 'Tested video output at 1080p, 4K@60Hz, and 4K@120Hz. HDR is fully functional. Left running stress test for 1 hour, temperatures normal.',
        timestamp: '2026-08-13T09:30:00Z',
        type: 'tech'
      },
      {
        id: 'n12',
        author: 'System',
        text: 'Status updated from Testing to Ready for Pickup.',
        timestamp: '2026-08-13T10:00:00Z',
        type: 'system'
      }
    ]
  },
  {
    id: 'RMA-2026-0004',
    customerName: 'Robert Dow',
    customerPhone: '+1 (555) 044-8811',
    customerEmail: 'robert.d@example.com',
    deviceCategory: 'Tablet',
    deviceBrand: 'Apple',
    deviceModel: 'iPad Air 5th Gen (M1, 2022)',
    serialNumber: 'DMPFG044Q16Y',
    reportedProblem: 'Device drains battery very fast, from 100% to 10% in under 1 hour. Get hot during simple web browsing.',
    diagnostics: 'Testing battery cycle count and capacity. Cycle count: 682, Max capacity: 74%. Significant software CPU usage noted, running hardware test.',
    priority: 'Low',
    status: 'Diagnostics',
    assignedTo: 'Marcus Vance (Senior Tech)',
    estimatedCost: 140,
    advancePayment: 0,
    actualCost: 0,
    createdAt: '2026-08-13T09:00:00Z',
    updatedAt: '2026-08-13T11:00:00Z',
    parts: [],
    notes: [
      {
        id: 'n13',
        author: 'System',
        text: 'RMA Ticket created and device received in Intake status.',
        timestamp: '2026-08-13T09:00:00Z',
        type: 'system'
      },
      {
        id: 'n14',
        author: 'Marcus Vance',
        text: 'Running diagnostic reports. Battery health is indeed degraded. I noticed background processes consuming abnormally high CPU cycles. Will wipe and test battery discharge rate under clean install.',
        timestamp: '2026-08-13T11:00:00Z',
        type: 'tech'
      }
    ]
  }
];

export const getRmaJobs = (): RmaJob[] => {
  const data = localStorage.getItem(STORAGE_KEY);
  if (!data) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(SEED_JOBS));
    return SEED_JOBS;
  }
  try {
    return JSON.parse(data);
  } catch (e) {
    console.error('Failed to parse RMA jobs from localStorage, resetting to seed data', e);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(SEED_JOBS));
    return SEED_JOBS;
  }
};

export const saveRmaJobs = (jobs: RmaJob[]): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
};

export const getRmaJobById = (id: string): RmaJob | undefined => {
  const jobs = getRmaJobs();
  return jobs.find(j => j.id === id);
};

export const createRmaJob = (jobData: Omit<RmaJob, 'id' | 'createdAt' | 'updatedAt' | 'notes' | 'parts'>): RmaJob => {
  const jobs = getRmaJobs();
  
  // Calculate next RMA ID
  let nextNum = 5;
  if (jobs.length > 0) {
    const ids = jobs.map(j => {
      const match = j.id.match(/RMA-\d+-(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    });
    nextNum = Math.max(...ids) + 1;
  }
  
  const paddedNum = String(nextNum).padStart(4, '0');
  const rmaId = `RMA-2026-${paddedNum}`;
  const now = new Date().toISOString();
  
  const newJob: RmaJob = {
    ...jobData,
    id: rmaId,
    createdAt: now,
    updatedAt: now,
    parts: [],
    images: jobData.images || [],
    notes: [
      {
        id: generateId(),
        author: 'System',
        text: `RMA Ticket created and device received in Intake status. Priority set to ${jobData.priority}.`,
        timestamp: now,
        type: 'system'
      }
    ]
  };
  
  jobs.push(newJob);
  saveRmaJobs(jobs);
  return newJob;
};

export const updateRmaJobStatus = (
  id: string, 
  newStatus: RmaStatus, 
  updatedBy: string, 
  statusChangeComment?: string
): RmaJob | undefined => {
  const jobs = getRmaJobs();
  const index = jobs.findIndex(j => j.id === id);
  if (index === -1) return undefined;
  
  const now = new Date().toISOString();
  const oldStatus = jobs[index].status;
  
  if (oldStatus === newStatus) return jobs[index];
  
  jobs[index].status = newStatus;
  jobs[index].updatedAt = now;
  
  // Append system log
  let commentStr = statusChangeComment ? ` Reason/Comment: "${statusChangeComment}"` : '';
  jobs[index].notes.push({
    id: generateId(),
    author: 'System',
    text: `Status updated from ${oldStatus} to ${newStatus} by ${updatedBy}.${commentStr}`,
    timestamp: now,
    type: 'system'
  });
  
  saveRmaJobs(jobs);
  return jobs[index];
};

export const updateRmaJobDiagnostics = (id: string, diagnostics: string, technician: string): RmaJob | undefined => {
  const jobs = getRmaJobs();
  const index = jobs.findIndex(j => j.id === id);
  if (index === -1) return undefined;
  
  const now = new Date().toISOString();
  
  jobs[index].diagnostics = diagnostics;
  jobs[index].updatedAt = now;
  
  jobs[index].notes.push({
    id: generateId(),
    author: technician,
    text: `Updated diagnostic findings. Diagnostic field edited.`,
    timestamp: now,
    type: 'tech'
  });
  
  saveRmaJobs(jobs);
  return jobs[index];
};

export const addRmaJobNote = (
  id: string, 
  text: string, 
  author: string, 
  type: JobNote['type']
): RmaJob | undefined => {
  const jobs = getRmaJobs();
  const index = jobs.findIndex(j => j.id === id);
  if (index === -1) return undefined;
  
  const now = new Date().toISOString();
  
  jobs[index].notes.push({
    id: generateId(),
    author,
    text,
    timestamp: now,
    type
  });
  jobs[index].updatedAt = now;
  
  saveRmaJobs(jobs);
  return jobs[index];
};

export const addRmaPart = (id: string, partData: Omit<RmaPart, 'id'>): RmaJob | undefined => {
  const jobs = getRmaJobs();
  const index = jobs.findIndex(j => j.id === id);
  if (index === -1) return undefined;
  
  const now = new Date().toISOString();
  const newPart: RmaPart = {
    ...partData,
    id: generateId()
  };
  
  jobs[index].parts.push(newPart);
  
  // Update actual/estimated costs based on new parts list
  const partsSum = jobs[index].parts.reduce((acc, p) => acc + (p.cost * p.qty), 0);
  // We keep a baseline margin if needed, but let's just make the actualCost align with parts cost + service fee
  // If the user specified estimatedCost, we can update actualCost to reflect standard parts billing
  const serviceFee = 80; // Standard bench diagnostic/labor fee
  jobs[index].actualCost = partsSum + serviceFee;
  jobs[index].updatedAt = now;
  
  jobs[index].notes.push({
    id: generateId(),
    author: 'System',
    text: `Part added: ${newPart.name} (Qty: ${newPart.qty}, Cost: $${newPart.cost}). Billing updated.`,
    timestamp: now,
    type: 'system'
  });
  
  saveRmaJobs(jobs);
  return jobs[index];
};

export const removeRmaPart = (id: string, partId: string): RmaJob | undefined => {
  const jobs = getRmaJobs();
  const index = jobs.findIndex(j => j.id === id);
  if (index === -1) return undefined;
  
  const now = new Date().toISOString();
  const partToRemove = jobs[index].parts.find(p => p.id === partId);
  if (!partToRemove) return jobs[index];
  
  jobs[index].parts = jobs[index].parts.filter(p => p.id !== partId);
  
  const partsSum = jobs[index].parts.reduce((acc, p) => acc + (p.cost * p.qty), 0);
  const serviceFee = 80;
  jobs[index].actualCost = jobs[index].parts.length > 0 ? partsSum + serviceFee : 0;
  jobs[index].updatedAt = now;
  
  jobs[index].notes.push({
    id: generateId(),
    author: 'System',
    text: `Part removed: ${partToRemove.name}. Billing updated.`,
    timestamp: now,
    type: 'system'
  });
  
  saveRmaJobs(jobs);
  return jobs[index];
};

export const deleteRmaJob = (id: string): boolean => {
  const jobs = getRmaJobs();
  const filtered = jobs.filter(j => j.id !== id);
  if (filtered.length === jobs.length) return false;
  saveRmaJobs(filtered);
  return true;
};

export const addRmaJobImage = (id: string, base64Data: string): RmaJob | undefined => {
  const jobs = getRmaJobs();
  const index = jobs.findIndex(j => j.id === id);
  if (index === -1) return undefined;
  
  const now = new Date().toISOString();
  
  if (!jobs[index].images) {
    jobs[index].images = [];
  }
  
  jobs[index].images.push(base64Data);
  jobs[index].updatedAt = now;
  
  jobs[index].notes.push({
    id: generateId(),
    author: 'System',
    text: 'Technician attached a new diagnostic photo/document to this ticket.',
    timestamp: now,
    type: 'system'
  });
  
  saveRmaJobs(jobs);
  return jobs[index];
};

export const removeRmaJobImage = (id: string, imageIndex: number): RmaJob | undefined => {
  const jobs = getRmaJobs();
  const index = jobs.findIndex(j => j.id === id);
  if (index === -1) return undefined;
  
  const now = new Date().toISOString();
  
  if (jobs[index].images && jobs[index].images.length > imageIndex) {
    jobs[index].images.splice(imageIndex, 1);
    jobs[index].updatedAt = now;
    
    jobs[index].notes.push({
      id: generateId(),
      author: 'System',
      text: 'Technician removed an attached photo from this ticket.',
      timestamp: now,
      type: 'system'
    });
    
    saveRmaJobs(jobs);
  }
  
  return jobs[index];
};
